import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Search, MoreHorizontal, UserX, UserCheck, Mail, Calendar, Filter, Download, ExternalLink, Shield, Trash2, Key } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            toast.error('Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const toggleUserStatus = async (user) => {
        const newStatus = user.status === 'active' ? 'suspended' : 'active';
        try {
            await api.put(`/admin/users/${user.id}`, { status: newStatus });
            toast.success(`Usuario ${newStatus === 'active' ? 'reactivado' : 'suspendido'}`);
            fetchUsers();
        } catch (error) {
            toast.error('Error al actualizar estado');
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`¿Estás seguro de eliminar a ${user.name}? Esta acción es irreversible.`)) return;
        try {
            await api.delete(`/admin/users/${user.id}`);
            toast.success('Usuario eliminado');
            fetchUsers();
        } catch (error) {
            toast.error('Error al eliminar usuario');
        }
    };

    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o email..."
                            className="pl-10 pr-4 py-2 w-full bg-white border border-slate-200 rounded-lg text-sm focus:ring-wedding-primary transition-all"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                            <Filter className="h-4 w-4 mr-2" /> Filtrar
                        </Button>
                        <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-2" /> Exportar
                        </Button>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <Table>
                        <TableHeader className="bg-slate-50">
                            <TableRow>
                                <TableHead className="w-[80px]">Avatar</TableHead>
                                <TableHead>Nombre / Email</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead>Rol</TableHead>
                                <TableHead>Plan</TableHead>
                                <TableHead>Registro</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [1, 2, 3, 4, 5].map(i => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={7} className="h-16 animate-pulse bg-slate-50/50"></TableCell>
                                    </TableRow>
                                ))
                            ) : filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <TableRow key={user.id} className="hover:bg-slate-50 transition-colors">
                                        <TableCell>
                                            <div className="h-10 w-10 rounded-full bg-wedding-primary/10 text-wedding-primary flex items-center justify-center font-bold">
                                                {user.name[0]}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span className="font-semibold text-slate-800">{user.name}</span>
                                                <span className="text-xs text-slate-500 flex items-center">
                                                    <Mail className="h-3 w-3 mr-1" /> {user.email}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {user.status === 'active' ? (
                                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Activo</Badge>
                                            ) : (
                                                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Suspendido</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary" className="capitalize">{user.role}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-sm font-medium">Evento Único</span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-xs text-slate-500 whitespace-nowrap">
                                                <Calendar className="h-3 w-3 inline mr-1" />
                                                {new Date(user.created_at).toLocaleDateString()}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon">
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="w-48">
                                                    <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                                                    <DropdownMenuItem>
                                                        <ExternalLink className="mr-2 h-4 w-4" /> Ver Detalle
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => toggleUserStatus(user)}>
                                                        {user.status === 'active' ? (
                                                            <><UserX className="mr-2 h-4 w-4 text-red-600" /> <span className="text-red-600">Suspender</span></>
                                                        ) : (
                                                            <><UserCheck className="mr-2 h-4 w-4 text-green-600" /> <span className="text-green-600">Activar</span></>
                                                        )}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem>
                                                        <Key className="mr-2 h-4 w-4" /> Resetear Acceso
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem className="text-red-600" onClick={() => deleteUser(user)}>
                                                        <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                                        No se encontraron usuarios
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminUsers;
